import { ModelError, State } from 'models';
import { HTTP_STATUS, RequestType } from 'models/appModels/Request';

export const errorOr = async <T,>(
  response: Response,
  rejectWithValue: (error: ModelError) => any,
  ignoreHttpError?: Array<number>,
  isBlob = false,
  isEmpty = false
) => {
  if (response.status === HTTP_STATUS.NO_CONTENT || (response.status === HTTP_STATUS.CREATED && isEmpty)) {
    return;
  }

  try {
    const result = isBlob ? URL.createObjectURL(await response.blob()) : await response.json();

    if (response.ok) {
      const eTag = response.headers.get('etag');
      if (eTag) {
        result.eTag = eTag;
      }

      return result as T & { eTag?: string };
    }

    result.ignoreHttpError = ignoreHttpError;
    return rejectWithValue(result);
  } catch (error: any) {
    const date = new Date();
    const offset = new Date(date.setMinutes(date.getMinutes() - date.getTimezoneOffset()));
    const timestamp = offset.toISOString();

    const modelError: ModelError = {
      ignoreHttpError,
      status: response.status,
      message: `${error.name}: ${error.message}`,
      path: response.url,
      timestamp
    };

    return rejectWithValue(modelError);
  }
};

export type PendingActionType = { payload: undefined; type: string } & { meta: { requestId: string } };

export const pending = <T,>(state: State<T>, action: PendingActionType) => {
  if (state.requestStatus !== 'pending') {
    state.requestStatus = 'pending';
    state.currentRequestId = action.meta.requestId;
    state.error = undefined;
    state.requestType = action.type.split('/')[0] as RequestType;
  }
};

export type FulfilledActionType<T> = { payload: T; type: string } & { meta: { requestId: string } };

export const fulfilled = <T,>(state: State<T>, action: FulfilledActionType<T>) => {
  const { requestId } = action.meta;
  if (state.requestStatus === 'pending' && state.currentRequestId === requestId) {
    state.data = action.payload;
    state.currentRequestId = undefined;
    state.requestStatus = 'idle';
    state.requestType = action.type.split('/')[0] as RequestType;
  }
};

export type RejectedActionType = { payload: ModelError | undefined; type: string } & { meta: { requestId: string } };

export const rejected = <T,>(state: State<T>, action: RejectedActionType) => {
  const { requestId } = action.meta;
  if (state.requestStatus === 'pending' && state.currentRequestId === requestId) {
    state.requestStatus = 'rejected';
    state.error = action.payload;
    state.currentRequestId = undefined;
    state.requestType = action.type.split('/')[0] as RequestType;
  }
};
